/**
 * The detail behind one square on the pickup roster's attendance strip.
 *
 * A square can only say *what* a day was. This says who checked the child in, who
 * collected them, who released them and at what times — and then keeps going backwards
 * through the student's history, so the roster answers "what happened last Tuesday"
 * without ever having to render a sixth square.
 */

import { useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import {
  TIMELINE_PAGE_SIZE,
  useStudentAttendanceTimeline,
  type TimelineDay,
  type TimelineRecord,
} from '../../hooks/useStudentAttendanceTimeline';
import { SLOT_LABEL, SLOT_SWATCH, type AttendanceSlotStatus } from '../../utils/attendanceSlot';
import { useStudentPhotoUrls } from '../../hooks/useStudentPhotoUrls';
import { StudentAvatar } from '../../components/StudentAvatar';
import { StudentPhotoLightbox } from '../../components/StudentPhotoLightbox';
import { formatTimeInBusinessTZ } from '../../utils/date';

interface Props {
  studentId: string;
  studentName: string;
  /**
   * Object path of the student's headshot, or null when none is on file. Signed here rather
   * than on the roster: the grid stays text-only, so exactly one photo is ever needed — the
   * student whose record is open.
   */
  photoPath?: string | null;
  /** The day whose square was clicked — highlighted, but never the only one shown. */
  focusDate: string;
  onClose: () => void;
}

/** Chip text colours, in the same family as the swatch beside them. */
const CHIP_TEXT: Record<AttendanceSlotStatus, string> = {
  checked_in: 'bg-green-50 text-green-800',
  checked_out: 'bg-green-50 text-green-800',
  absent: 'bg-red-50 text-red-700',
  not_scheduled: 'bg-gray-50 text-gray-500',
  no_record: 'bg-gray-50 text-gray-400',
  // The timeline is built from days that have records, so neither of these normally
  // reaches a chip — they exist because the roster's week window can show days that have
  // not happened or on which nothing ran.
  upcoming: 'bg-gray-50 text-gray-400',
  not_enrolled: 'bg-gray-50 text-gray-400',
  no_session: 'bg-gray-50 text-gray-400',
};

function StatusChip({ status }: { status: AttendanceSlotStatus }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${CHIP_TEXT[status]}`}
    >
      <span className={`w-2 h-2 rounded-sm flex-shrink-0 ${SLOT_SWATCH[status]}`} />
      {SLOT_LABEL[status]}
    </span>
  );
}

/** "Tue, Aug 11" — a DATE column, so it is split rather than parsed as an instant. */
function formatDayHeading(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function clockTime(iso: string): string {
  return formatTimeInBusinessTZ(iso, { hour12: true });
}

/**
 * Everything that was recorded against one program's row, one fact per line.
 *
 * A checked-out student keeps the whole day's sequence — arrival, departure, and both
 * names — because "when did they get here" is still worth answering after they have gone
 * home. Phrasing matches the teacher portal's row exactly; this is the same event.
 */
function RecordFacts({ record, showProgram }: { record: TimelineRecord; showProgram: boolean }) {
  const facts: { key: string; node: React.ReactNode }[] = [];

  if (record.checkedInAt && (record.status === 'checked_in' || record.status === 'picked_up')) {
    const who = record.checkedInBy === 'parent' ? 'parent' : record.checkedInByName || 'teacher';
    facts.push({ key: 'in', node: <>Checked in by {who} · {clockTime(record.checkedInAt)}</> });
  }

  if (record.status === 'picked_up') {
    const collector = record.pickedUpByName ?? 'someone unrecorded';
    facts.push({
      key: 'out',
      node: (
        <>
          Checked out to{' '}
          {record.pickupIsAuthorized === false ? (
            <span className="inline-flex items-center gap-1 text-amber-700">
              {collector}
              <AlertTriangle className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
              <span className="sr-only">not on the authorized pickup list</span>
            </span>
          ) : (
            collector
          )}
          {record.pickedUpAt ? ` · ${clockTime(record.pickedUpAt)}` : ''}
        </>
      ),
    });
    if (record.pickupRecordedByName) {
      facts.push({ key: 'released', node: <>Released by {record.pickupRecordedByName}</> });
    }
  }

  if (record.status === 'absent') {
    facts.push({ key: 'absent', node: <>Absent{record.absenceReason ? ` — ${record.absenceReason}` : ''}</> });
  }

  if (record.status === 'not_arrived') {
    facts.push({ key: 'none', node: <span className="text-gray-400">Nobody marked this student either way.</span> });
  }

  return (
    <div className="space-y-0.5">
      {showProgram && (
        <p className="text-[11px] font-medium text-gray-600">{record.programName ?? 'Unknown program'}</p>
      )}
      {facts.map(fact => (
        <p key={fact.key} className="text-xs leading-snug text-gray-600">
          {fact.node}
        </p>
      ))}
    </div>
  );
}

function DayEntry({ day, isFocused }: { day: TimelineDay; isFocused: boolean }) {
  return (
    <li
      className={`relative pl-6 py-2.5 ${isFocused ? 'bg-blue-50/60 -mx-3 px-3 pl-9 rounded-lg' : ''}`}
      aria-current={isFocused ? 'true' : undefined}
    >
      {/* Timeline rail dot, aligned with the heading's first line. */}
      <span
        className={`absolute top-4 w-2 h-2 rounded-full ring-2 ring-white ${
          isFocused ? 'left-[1.375rem] bg-blue-500' : 'left-[0.1875rem] bg-gray-300'
        }`}
        aria-hidden="true"
      />
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="text-[13px] font-semibold text-gray-900">{formatDayHeading(day.date)}</span>
        <StatusChip status={day.status} />
      </div>
      <div className="mt-1 space-y-1.5">
        {day.status === 'not_scheduled' ? (
          <p className="text-xs text-gray-500">Not one of this student's selected Flex days.</p>
        ) : day.records.length === 0 ? (
          <p className="text-xs text-gray-400">No attendance row for this day.</p>
        ) : (
          day.records.map(record => (
            <RecordFacts key={record.id} record={record} showProgram={day.records.length > 1} />
          ))
        )}
      </div>
    </li>
  );
}

export function AttendanceRecordModal({ studentId, studentName, photoPath, focusDate, onClose }: Props) {
  const { data: photoUrls = {} } = useStudentPhotoUrls([photoPath]);
  const photoUrl = photoPath ? photoUrls[photoPath] : null;
  const [showPhoto, setShowPhoto] = useState(false);
  const [sessions, setSessions] = useState(TIMELINE_PAGE_SIZE);
  const { data, isLoading, isFetching, error } = useStudentAttendanceTimeline(studentId, sessions);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      // The enlarged photo is a layer above this one and listens on `window` too. Without
      // this guard a single Escape would dismiss both, throwing away the record the admin
      // was reading just because they closed the photo.
      if (showPhoto) return;
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, showPhoto]);

  const days = data?.days ?? [];
  // The clicked day is normally in the first page, but a student whose record was seeded
  // late can push it out; showing it pinned on top keeps the modal about the square that
  // opened it either way.
  const focusMissing = !isLoading && !days.some(day => day.date === focusDate);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex flex-col justify-end sm:items-center sm:justify-center z-50 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Attendance history for ${studentName}`}
      onClick={event => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-t-2xl sm:rounded-xl w-full sm:max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between gap-2 px-5 pt-5 pb-3 border-b border-gray-100">
          <div className="flex items-center gap-3 min-w-0">
            <StudentAvatar
              name={studentName}
              photoUrl={photoUrl}
              size="lg"
              onEnlarge={photoUrl ? () => setShowPhoto(true) : undefined}
            />
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900 truncate">{studentName}</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Attendance history · {formatDayHeading(focusDate)} selected
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-2">
          {error ? (
            <p className="py-6 text-sm text-red-700">
              Could not load this student's attendance:{' '}
              {error instanceof Error ? error.message : 'unknown error'}
            </p>
          ) : isLoading ? (
            <div className="py-3 space-y-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-14 bg-gray-100 animate-pulse rounded-lg" />
              ))}
            </div>
          ) : days.length === 0 ? (
            <p className="py-6 text-sm text-gray-500">
              No attendance has been recorded for this student yet.
            </p>
          ) : (
            <>
              {focusMissing && (
                <p className="mt-3 mb-1 px-3 py-2 text-xs bg-amber-50 text-amber-800 rounded border border-amber-200">
                  No attendance row exists for {formatDayHeading(focusDate)}. The sessions below
                  are the days this student does have records for.
                </p>
              )}
              <ol className="relative border-l border-gray-200 ml-1 my-2">
                {days.map(day => (
                  <DayEntry key={day.date} day={day} isFocused={day.date === focusDate} />
                ))}
              </ol>
            </>
          )}
        </div>

        {data?.hasMore && (
          <div className="px-5 py-3 border-t border-gray-100">
            <button
              type="button"
              onClick={() => setSessions(current => current + TIMELINE_PAGE_SIZE)}
              disabled={isFetching}
              className="w-full px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {isFetching ? 'Loading…' : 'Show earlier sessions'}
            </button>
          </div>
        )}
      </div>

      {showPhoto && photoUrl && (
        <StudentPhotoLightbox
          name={studentName}
          photoUrl={photoUrl}
          onClose={() => setShowPhoto(false)}
        />
      )}
    </div>
  );
}
